import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import * as LucideIcons from 'lucide-react';
import { Search, ChevronDown } from 'lucide-react';

const POPULAR_ICONS = [
  'Award', 'Star', 'Trophy', 'Medal', 'Crown', 'Gem', 'Heart', 'ThumbsUp',
  'Zap', 'Flame', 'Target', 'Badge', 'Shield', 'Gift', 'Coins', 'Wallet',
  'Users', 'User', 'UserCheck', 'UserPlus', 'HandHeart', 'Sparkles',
  'Store', 'Home', 'MapPin', 'Compass', 'Map', 'Route', 'Building',
  'Utensils', 'UtensilsCrossed', 'Coffee', 'Pizza', 'Cookie', 'Cake',
  'Camera', 'Image', 'Video', 'Mic', 'Share', 'MessageCircle', 'Bell',
  'Calendar', 'Clock', 'Timer', 'TrendingUp', 'BarChart', 'Activity',
  'CheckCircle', 'Check', 'CircleDot', 'Verified', 'BadgeCheck',
  'Footprints', 'Navigation', 'Send', 'Bookmark', 'Flag', 'Tag',
  'PenTool', 'FileText', 'BookOpen', 'Scroll', 'ClipboardCheck',
  'Sun', 'Moon', 'Rainbow', 'PartyPopper', 'Confetti', 'Music',
];

interface LucideIconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
}

export function LucideIconPicker({ value, onChange }: LucideIconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const IconComponent = (LucideIcons as any)[value] || LucideIcons.Award;

  const filteredIcons = useMemo(() => {
    const searchLower = search.toLowerCase();
    if (!searchLower) return POPULAR_ICONS;
    
    return POPULAR_ICONS.filter(name => 
      name.toLowerCase().includes(searchLower)
    );
  }, [search]);

  const handleSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full justify-between"
          data-testid="icon-picker-trigger"
        >
          <div className="flex items-center gap-2">
            <IconComponent className="w-4 h-4" />
            <span>{value}</span>
          </div>
          <ChevronDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="icon-search-input"
            />
          </div>
        </div>
        <ScrollArea className="h-64">
          <div className="grid grid-cols-6 gap-1 p-2">
            {filteredIcons.map((iconName) => {
              const Icon = (LucideIcons as any)[iconName];
              if (!Icon) return null;
              
              return (
                <Button
                  key={iconName}
                  variant={value === iconName ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => handleSelect(iconName)}
                  title={iconName}
                  data-testid={`icon-option-${iconName}`}
                >
                  <Icon className="w-5 h-5" />
                </Button>
              );
            })}
          </div>
          {filteredIcons.length === 0 && (
            <div className="p-4 text-center text-muted-foreground text-sm">
              No icons found
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
